import React, { useContext, useMemo, useState } from 'react';
import AuthContext from '../../../store/auth-context';
import {
    ActionNode,
    InputNode,
    SchemaNode,
    SectionNode,
    ViewNode,
} from './types';
import { Checkbox } from '../../ui/checkbox';
import { Label } from '../../ui/label';
import { Check } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { TotalSummary } from '../../documents/form/views/cpt/TotalSummary';
import type { CaseReport } from '../../../services/reportService';
import {
    collectNodeIdCounts,
    normalizePreviewDocuments,
    PreviewRenderDocument,
    withSourceLabelIfDuplicate,
} from './utils/previewMergeUtils';
import { PreviewShell } from '../../preview/PreviewShell';
import {
    PreviewFooter,
    PreviewRunningHeader,
    SectionEyebrow,
    PreviewField,
    PreviewRichText,
    formatPreviewDate,
    previewTheme,
    type PreviewHeaderConfig,
    type PreviewMode,
} from '@nordinsight/implant-core/ui';
import { generateTextReport } from './utils/cptDocumentViewUtils';
import {
    formatArray,
    formatBoolean,
    resolveDataSourceLabel,
} from './utils/cptPreviewFormatters';

interface CptReportPreviewProps {
    documents: PreviewRenderDocument[];
    dataSources?: Record<string, any[]>;
    className?: string;
    onClose?: () => void;
    applicableCodes?: CaseReport['cptEligibleCodes'];
    caseId?: string;
}

interface RenderContext {
    docData: Record<string, any>;
    sourceLabel: string;
    sourceKey: string;
    localDataSources: Record<string, any[]>;
}

const SIGNING_QHP_SECTION_ID = 'signingQhp';

interface SigningBlockEntry {
    sourceLabel: string;
    sourceKey: string;
    name: string;
    date: string;
}

export const CptReportPreview: React.FC<CptReportPreviewProps> = ({
    documents,
    dataSources,
    className,
    onClose,
    applicableCodes,
    caseId,
}) => {
    const ctx = useContext(AuthContext);
    const [mode, setMode] = useState<PreviewMode>('print');
    const [includeApplicableCodes, setIncludeApplicableCodes] = useState(true);
    const [signatureToggles, setSignatureToggles] = useState<
        Record<string, boolean>
    >(() => {
        const initial: Record<string, boolean> = {};
        documents.forEach((d) => {
            const hasSignOffSection = d?.schema?.sections?.some(
                (s) => s.id === SIGNING_QHP_SECTION_ID
            );
            if (!hasSignOffSection) return;
            const sourceKey = d.sourceKey || d.schema?.id || '';
            const v = d?.data?.signingQhpName;
            initial[sourceKey] = typeof v === 'string' && v.trim() !== '';
        });
        return initial;
    });

    const normalizedDocuments = useMemo(
        () => normalizePreviewDocuments(documents),
        [documents]
    );
    const duplicateIdCounts = useMemo(
        () => collectNodeIdCounts(normalizedDocuments),
        [normalizedDocuments]
    );

    const hasPreviewContent = normalizedDocuments.length > 0;
    const applicableCodeItems = useMemo(
        () =>
            (
                [
                    {
                        code: '76014',
                        count: Number(applicableCodes?.['76014'] ?? 0),
                        label: 'CPT 76014',
                    },
                    {
                        code: '76015',
                        count: Number(applicableCodes?.['76015'] ?? 0),
                        label: 'CPT 76015',
                    },
                    {
                        code: '76016',
                        count: Number(applicableCodes?.['76016'] ?? 0),
                        label: 'CPT 76016',
                    },
                    {
                        code: '76018',
                        count: Number(applicableCodes?.['76018'] ?? 0),
                        label: 'CPT 76018',
                    },
                    {
                        code: '76019',
                        count: Number(applicableCodes?.['76019'] ?? 0),
                        label: 'CPT 76019',
                    },
                ] as const
            ).filter((item) => item.count > 0),
        [applicableCodes]
    );
    const hasApplicableCodes = applicableCodeItems.length > 0;
    const signingBlocks = useMemo<SigningBlockEntry[]>(() => {
        return normalizedDocuments
            .filter((document) => {
                const sections = document.schema?.sections || [];
                return sections.some(
                    (section) => section.id === SIGNING_QHP_SECTION_ID
                );
            })
            .map((document) => {
                const rawName = document.data?.signingQhpName;
                const name = typeof rawName === 'string' ? rawName.trim() : '';
                const date = formatPreviewDate(document.data?.signingQhpDate);
                const sourceLabel =
                    document.sourceLabel ||
                    document.schema.title ||
                    document.schema.id;
                const sourceKey =
                    document.sourceKey || document.schema.id || '';
                return { sourceLabel, sourceKey, name, date };
            });
    }, [normalizedDocuments]);

    const getValue = (fieldId: string, context: RenderContext) => {
        return context.docData[fieldId];
    };

    const getNodeTitle = (
        title: string,
        nodeId: string,
        context: RenderContext
    ) => {
        return withSourceLabelIfDuplicate(
            title,
            nodeId,
            context.sourceLabel,
            duplicateIdCounts
        );
    };

    const renderValue = (
        field: InputNode,
        value: any,
        context: RenderContext
    ) => {
        if (value === undefined || value === null || value === '') return null;

        const dataSourceKey = field.data?.options?.source;
        if (dataSourceKey && context.localDataSources?.[dataSourceKey]) {
            const resolved = resolveDataSourceLabel(
                value,
                context.localDataSources[dataSourceKey]
            );
            if (resolved) return resolved;
        }

        if (field.inputType === 'richtext') {
            return <PreviewRichText html={value} />;
        }

        if (field.inputType === 'repeater') {
            if (!Array.isArray(value) || value.length === 0) return null;

            if (field.id === 'implants') {
                return (
                    <div className="space-y-2">
                        {value.map((item: any, index: number) => {
                            const name =
                                item._implantModel ||
                                item.implantName ||
                                'Unknown Implant';
                            const manufacturer = item._implantManufacturer;

                            return (
                                <div
                                    key={`${context.sourceKey}:${field.id}:implant:${index}`}
                                    className="border border-[#d2d2d2] px-3.5 py-3 text-[12.5px]"
                                >
                                    <div className="text-sm">
                                        <span className="mr-1.5 font-medium text-[#888]">
                                            Name/Model:
                                        </span>
                                        <span>{name}</span>
                                    </div>
                                    {manufacturer && (
                                        <div className="text-sm">
                                            <span className="mr-1.5 font-medium text-[#888]">
                                                Manufacturer:
                                            </span>
                                            <span>{manufacturer}</span>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                );
            }

            return (
                <div className="space-y-2">
                    {value.map((item: any, index: number) => {
                        const details = Object.entries(item)
                            .filter(
                                ([key]) =>
                                    key !== 'id' &&
                                    key !== '_id' &&
                                    !key.startsWith('_')
                            )
                            .map(([key, fieldValue]) => {
                                const subField = field.fields?.find(
                                    (childField) => childField.id === key
                                );
                                const label = subField?.title || key;

                                return (
                                    <div key={key} className="text-sm">
                                        <span className="mr-1.5 font-medium text-[#888]">
                                            {label}:
                                        </span>
                                        <span>{String(fieldValue)}</span>
                                    </div>
                                );
                            });

                        return (
                            <div
                                key={`${context.sourceKey}:${field.id}:row:${index}`}
                                className="p-3 print:bg-muted/20 rounded border text-sm"
                            >
                                {details}
                            </div>
                        );
                    })}
                </div>
            );
        }

        if (field.inputType === 'date') {
            return formatPreviewDate(value);
        }

        if (Array.isArray(value)) {
            return formatArray(value);
        }

        if (typeof value === 'boolean') {
            return formatBoolean(value);
        }

        return <span className="whitespace-pre-wrap">{value}</span>;
    };

    const renderAction = (
        field: ActionNode,
        context: RenderContext,
        nodeKey: string
    ) => {
        if (field.actionType !== 'checkbox') {
            return null;
        }

        const value = getValue(field.id, context);
        if (!value) return null;

        const fieldTitle = getNodeTitle(field.title, field.id, context);
        const signedBy = getValue(`${field.id}By`, context);
        const signedAtRaw = getValue(`${field.id}At`, context);
        const signedAt = (() => {
            if (typeof signedAtRaw !== 'string' || !signedAtRaw) return '';
            const parsed = new Date(signedAtRaw);
            return Number.isNaN(parsed.getTime())
                ? signedAtRaw
                : parsed.toLocaleString();
        })();

        return (
            <div
                key={nodeKey}
                className="mt-4 border border-[#d2d2d2] border-l-[5px] border-l-spot px-[18px] py-3"
            >
                <div className="flex items-center gap-2.5">
                    <Check
                        className="h-4 w-4 shrink-0 text-spot"
                        strokeWidth={2.6}
                    />
                    <span className="text-[13px] font-bold">{fieldTitle}</span>
                </div>
                {signedBy && (
                    <div className="mt-1 pl-[26px] text-[12px] text-[#555]">
                        Signed by {signedBy}
                        {signedAt ? ` on ${signedAt}` : ''}
                    </div>
                )}
            </div>
        );
    };

    const renderView = (
        field: ViewNode,
        context: RenderContext,
        nodeKey: string
    ) => {
        const fieldTitle = getNodeTitle(field.title, field.id, context);

        if (field.viewType === 'total-summary') {
            const dependencyName = field.viewProps?.dependency || 'allTasks';
            const dependentValue = context.docData[dependencyName];
            if (!dependentValue || dependentValue.length === 0) return null;

            return (
                <div key={nodeKey} className="mb-4">
                    <h4 className="mb-2 text-[12px] font-bold">{fieldTitle}</h4>
                    <TotalSummary
                        value={dependentValue}
                        sumField={field.viewProps?.sumField || 'minutes'}
                        className="border border-[#d2d2d2] bg-white"
                        {...field.viewProps}
                    />
                </div>
            );
        }

        if (field.viewType === 'timeline') {
            return null;
        }

        if (field.viewType === 'cpt-codes') {
            return null;
        }

        if (field.viewType === 'context-list') {
            const dataSource =
                typeof field.viewProps?.dataSource === 'string'
                    ? field.viewProps.dataSource
                    : '';
            const allSourceData = dataSource
                ? context.docData[dataSource]
                : undefined;
            if (!Array.isArray(allSourceData) || allSourceData.length === 0)
                return null;

            const excludedKey = dataSource ? `_excluded_${dataSource}` : '';
            const excludedIndices: number[] = excludedKey
                ? (context.docData[excludedKey] as number[]) || []
                : [];
            const sourceData =
                excludedIndices.length > 0
                    ? allSourceData.filter(
                          (_: any, i: number) => !excludedIndices.includes(i)
                      )
                    : allSourceData;

            if (sourceData.length === 0) return null;

            const displayFields: Array<{ key: string; label: string }> =
                field.viewProps?.displayFields || [];

            return (
                <div key={nodeKey} className="mb-4">
                    <h4 className="mb-2 text-[12px] font-bold">{fieldTitle}</h4>
                    <div className="space-y-2">
                        {sourceData.map((item: any, index: number) => {
                            const fields = displayFields
                                .map((df) => {
                                    const val = item?.[df.key];
                                    if (
                                        val === undefined ||
                                        val === null ||
                                        val === ''
                                    )
                                        return null;
                                    return {
                                        label: df.label,
                                        value: String(val),
                                    };
                                })
                                .filter(Boolean) as Array<{
                                label: string;
                                value: string;
                            }>;

                            if (fields.length === 0) return null;

                            return (
                                <div
                                    key={`${context.sourceKey}:${field.id}:ctx:${index}`}
                                    className="border border-[#d2d2d2] px-3.5 py-3 text-[12.5px]"
                                >
                                    {fields.map((f) => (
                                        <div key={f.label} className="text-sm">
                                            <span className="mr-1.5 font-medium text-[#888]">
                                                {f.label}:
                                            </span>
                                            <span>{f.value}</span>
                                        </div>
                                    ))}
                                </div>
                            );
                        })}
                    </div>
                </div>
            );
        }

        return null;
    };

    const renderInput = (
        field: InputNode,
        context: RenderContext,
        nodeKey: string
    ) => {
        const value = getValue(field.id, context);
        if (value === undefined || value === null || value === '') return null;
        if (field.id === 'id' || field.id === '_id') return null;

        const fieldTitle = getNodeTitle(field.title, field.id, context);

        return (
            <PreviewField
                key={nodeKey}
                field={{
                    key: nodeKey,
                    label: fieldTitle,
                    value: renderValue(field, value, context),
                }}
            />
        );
    };

    const renderSection = (
        section: SectionNode,
        context: RenderContext,
        nodeKey: string
    ) => {
        if (section.sectionType === 'actions') {
            return null;
        }

        if (section.sectionType === 'sign-off') {
            if (!signatureToggles[context.sourceKey]) return null;

            const name = context.docData.signingQhpName;
            const displayName = typeof name === 'string' ? name.trim() : '';
            const date = formatPreviewDate(context.docData.signingQhpDate);

            return (
                <div
                    key={nodeKey}
                    className={cn(
                        previewTheme.callout,
                        'mb-6 mt-6 px-[18px] py-4'
                    )}
                >
                    <SectionEyebrow label={section.title} className="mb-4" />
                    <div className="space-y-6">
                        <div className="space-y-2">
                            <div className={previewTheme.fieldLabel}>
                                Qualified Healthcare Professional Name
                            </div>
                            <div className="min-h-8 border-b border-[#1a1a1a] pb-1 text-[13px]">
                                {displayName}
                            </div>
                        </div>
                        <div className="grid gap-6 sm:grid-cols-[minmax(0,0.65fr)_minmax(0,1.35fr)]">
                            <div className="space-y-2">
                                <div className={previewTheme.fieldLabel}>
                                    Date
                                </div>
                                <div className="min-h-8 border-b border-[#1a1a1a] pb-1 text-[13px]">
                                    {date}
                                </div>
                            </div>
                            <div className="space-y-2">
                                <div className={previewTheme.fieldLabel}>
                                    Signature
                                </div>
                                <div className="min-h-8 border-b border-[#1a1a1a] pb-1 text-[13px]" />
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        const renderedEntries = section.fields
            .map((field, index) => ({
                node: field,
                element: renderNode(
                    field,
                    context,
                    `${nodeKey}:${field.id}:${index}`
                ),
            }))
            .filter((entry) => Boolean(entry.element));

        if (renderedEntries.length === 0) {
            return null;
        }

        const showBackground =
            section.background &&
            section.background.condition &&
            context.docData[section.background.condition];

        // Keep the heading together with the first field so a page break
        // never lands right after the heading. Repeaters are excluded —
        // they can be taller than a page and must be allowed to flow.
        const firstNode = renderedEntries[0].node;
        const groupFirstWithHeading =
            firstNode.kind === 'input' && firstNode.inputType !== 'repeater';
        const elements = renderedEntries.map((entry) => entry.element);

        const heading = (
            <SectionEyebrow label={section.title} className="mb-3.5" />
        );

        return (
            <div
                key={nodeKey}
                className={cn(
                    'mb-7',
                    showBackground ? 'border border-[#d2d2d2] px-4 py-3' : ''
                )}
            >
                {groupFirstWithHeading ? (
                    <>
                        <div className="break-inside-avoid">
                            {heading}
                            {elements[0]}
                        </div>
                        {elements.slice(1)}
                    </>
                ) : (
                    <>
                        {heading}
                        {elements}
                    </>
                )}
            </div>
        );
    };

    const renderNode = (
        node: SchemaNode,
        context: RenderContext,
        nodeKey: string
    ) => {
        const hasValue =
            context.docData[node.id] !== undefined &&
            context.docData[node.id] !== '' &&
            context.docData[node.id] !== null;

        if (!hasValue) {
            if (node.hidden === 'true') return null;
            if (node.hidden && context.docData[node.hidden] === true) {
                return null;
            }
        }

        if (node.kind === 'section')
            return renderSection(node, context, nodeKey);
        if (node.kind === 'input') return renderInput(node, context, nodeKey);
        if (node.kind === 'view') return renderView(node, context, nodeKey);
        if (node.kind === 'action') return renderAction(node, context, nodeKey);
        return null;
    };

    const plainText = useMemo(() => {
        const parts = normalizedDocuments.map((document) => {
            const title =
                document.sourceLabel ||
                document.schema.title ||
                document.schema.id ||
                '';
            const sourceKey = document.sourceKey || document.schema.id || '';
            const includeSignoff = signatureToggles[sourceKey] ?? false;
            const schema = includeSignoff
                ? document.schema
                : {
                      ...document.schema,
                      sections: document.schema.sections.filter(
                          (section) =>
                              section.sectionType !== 'sign-off' &&
                              section.id !== SIGNING_QHP_SECTION_ID
                      ),
                  };
            const body = generateTextReport(schema, document.data);
            return normalizedDocuments.length > 1 && title
                ? `${title.toUpperCase()}\n\n${body}`
                : body;
        });

        if (hasApplicableCodes && includeApplicableCodes) {
            parts.push(
                [
                    'APPLICABLE CODES',
                    ...applicableCodeItems.map(
                        (item) => `${item.count}x ${item.label}`
                    ),
                ].join('\n')
            );
        }

        return parts.join('\n\n');
    }, [
        normalizedDocuments,
        signatureToggles,
        hasApplicableCodes,
        includeApplicableCodes,
        applicableCodeItems,
    ]);

    const applicableCodesFieldId = React.useId();

    const renderApplicableCodesBlock = () => {
        if (
            !hasPreviewContent ||
            !hasApplicableCodes ||
            !includeApplicableCodes
        ) {
            return null;
        }

        return (
            <div className="mt-10">
                <SectionEyebrow label="Applicable Codes" className="mb-3.5" />
                <div>
                    {applicableCodeItems.map((item) => (
                        <div
                            key={item.code}
                            className={cn(previewTheme.row, 'text-[12.5px]')}
                        >
                            <span className="font-bold">{`${item.count}×`}</span>
                            <span>{item.label}</span>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const footerControls = (
        <>
            {hasApplicableCodes && (
                <div className="flex items-start gap-3 rounded-lg border border-border/70 bg-background/70 px-4 py-3 w-full">
                    <Checkbox
                        id={applicableCodesFieldId}
                        checked={includeApplicableCodes}
                        onCheckedChange={(checked) =>
                            setIncludeApplicableCodes(Boolean(checked))
                        }
                        disabled={!hasPreviewContent}
                        className="mt-1"
                    />
                    <div className="flex-1 space-y-1">
                        <Label
                            htmlFor={applicableCodesFieldId}
                            className="text-sm font-medium"
                        >
                            Add applicable codes section
                        </Label>
                        <div className="text-xs text-muted-foreground">
                            {applicableCodeItems
                                .map((item) => `${item.count}x ${item.label}`)
                                .join(', ')}
                        </div>
                    </div>
                </div>
            )}

            {signingBlocks.map((block) => {
                const checkboxId = `signature-toggle-${block.sourceKey}`;
                return (
                    <div
                        key={checkboxId}
                        className="flex items-start gap-3 rounded-lg border border-border/70 bg-background/70 px-4 py-3 w-full"
                    >
                        <Checkbox
                            id={checkboxId}
                            checked={signatureToggles[block.sourceKey] ?? false}
                            onCheckedChange={(checked) =>
                                setSignatureToggles((prev) => ({
                                    ...prev,
                                    [block.sourceKey]: Boolean(checked),
                                }))
                            }
                            disabled={!hasPreviewContent}
                            className="mt-1"
                        />
                        <div className="flex-1">
                            <Label
                                htmlFor={checkboxId}
                                className="text-sm font-medium"
                            >
                                {`Include signing QHP — ${block.sourceLabel}`}
                            </Label>
                        </div>
                    </div>
                );
            })}
        </>
    );

    const headerConfig = useMemo<PreviewHeaderConfig>(() => {
        return {
            title: '',
            eyebrow: 'CPT Reporting',
            generatedDate: new Date().toISOString().slice(0, 10),
            showBrand: false,
        };
    }, []);

    const printFooter = useMemo(() => {
        const facilityName = ctx.getContext()?.department?.name;
        const generatedAt = new Date().toLocaleDateString();
        return (
            <PreviewFooter
                left={['NordInsight', facilityName].filter(Boolean).join(' · ')}
                right={`Printed ${generatedAt}`}
            />
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ctx]);

    if (mode === 'copy') {
        return (
            <PreviewShell
                className={className}
                mode={mode}
                onModeChange={setMode}
                hasContent={hasPreviewContent}
                footerSlot={footerControls}
                generateCopyText={() => plainText}
                showPrint={false}
                onClose={onClose}
                previewSource="cpt"
            >
                <pre className="whitespace-pre-wrap font-mono text-sm leading-relaxed p-4">
                    {plainText}
                </pre>
            </PreviewShell>
        );
    }

    const runningHeader = (
        <PreviewRunningHeader
            label={caseId ? `Case ${caseId}` : 'CPT Report'}
        />
    );

    return (
        <PreviewShell
            className={className}
            mode={mode}
            onModeChange={setMode}
            header={headerConfig}
            hasContent={hasPreviewContent}
            footerSlot={footerControls}
            printHeader={runningHeader}
            printFooter={printFooter}
            generateCopyText={() => plainText}
            showCopy={false}
            onClose={onClose}
            previewSource="cpt"
        >
            {normalizedDocuments.map((document, documentIndex) => {
                const sourceKey =
                    document.sourceKey ||
                    `${document.schema.id || 'schema'}-${documentIndex}`;
                const sourceLabel =
                    document.sourceLabel ||
                    document.schema.title ||
                    document.schema.id;
                const context: RenderContext = {
                    docData: document.data,
                    sourceLabel,
                    sourceKey,
                    localDataSources: document.dataSources || dataSources || {},
                };

                return (
                    <div key={sourceKey}>
                        {normalizedDocuments.length > 1 && (
                            <h2
                                className={cn(
                                    'mb-4 border-b border-[#d2d2d2] pb-2 font-serif text-[24px] font-bold',
                                    documentIndex > 0 && 'mt-10'
                                )}
                            >
                                {document.schema.title}
                            </h2>
                        )}
                        {document.schema.sections.map((section, index) => {
                            if (section.sectionType === 'actions') {
                                return null;
                            }

                            return renderSection(
                                section,
                                context,
                                `${sourceKey}:${section.id}:${index}`
                            );
                        })}
                    </div>
                );
            })}
            {renderApplicableCodesBlock()}
        </PreviewShell>
    );
};
